import { useMemo } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import useIsMobile from '../../utils/hooks/useIsMobile';
import s from '../../styles/components/shows/FanMap.module.css';

const STEP = 4;
const MAX_R = 12;
const GOLDEN = 2.399963;

const stateStyle = {
  default: { outline: 'none' },
  hover: { outline: 'none' },
  pressed: { outline: 'none' },
};

// One tack per fan, spiraled around the area center and capped in radius.
// Within an area, lower tacks are drawn last so they sit on top.
function placeTacks(areas) {
  return areas.flatMap((area) =>
    area.fans
      .map((color, k) => {
        const r = Math.min(STEP * Math.sqrt(k), MAX_R);
        const angle = k * GOLDEN;
        return {
          key: `${area.id}-${k}`,
          color,
          center: area.center,
          dx: r * Math.cos(angle),
          dy: r * Math.sin(angle),
        };
      })
      .sort((a, b) => a.dy - b.dy)
  );
}

export default function FanMap({ data }) {
  const isMobile = useIsMobile();
  const tacks = useMemo(() => placeTacks(data.areas), [data.areas]);

  return (
    <section className={isMobile ? s.containerMobile : s.container}>
      <div className={s.header}>
        <span className={s.eyebrow}>{data.eyebrow}</span>
        <h1 className={isMobile ? s.titleMobile : s.title}>{data.title}</h1>
        <p className={s.description}>{data.description}</p>
      </div>

      <div className={s.mapWrap}>
        <ComposableMap projection="geoAlbersUsa" width={975} height={610} className={s.map}>
          <Geographies geography={data.src}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography key={geo.rsmKey} geography={geo} className={s.state} style={stateStyle} />
              ))
            }
          </Geographies>

          {tacks.map((tack) => (
            <Marker key={tack.key} coordinates={tack.center}>
              <g
                transform={`translate(${tack.dx}, ${tack.dy})`}
                style={{ filter: `drop-shadow(0 0 3px ${tack.color})` }}
              >
                <line y2={-5} className={s.stem} />
                <circle cy={-7} r={2.6} fill={tack.color} className={s.head} />
              </g>
            </Marker>
          ))}
        </ComposableMap>
      </div>
    </section>
  );
}